import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL;

type User = {
  [key: string]: string; // Flexible typing for all fields
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API_URL}/get-users`);
      if (!response.ok) throw new Error('Failed to fetch users.');
      const data = await response.json();
      setUsers(data);
    } catch (err: any) {
      setError(err.message || 'Error fetching users.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = () => {
    navigate('/');
  };

  const handleDelete = async (username: string) => {
    if ((username || '').toLowerCase() === 'pharmanest_aws') return;

    const updatedUsers = users.filter((user) => user.Username !== username);
    setUsers(updatedUsers);

    try {
      const response = await fetch(`${API_URL}/delete-user/${username}`, {
        method: 'DELETE',
      });

      if (!response.ok) throw new Error('Failed to delete user.');

      const updatedListRes = await fetch(`${API_URL}/get-users`);
      if (!updatedListRes.ok) throw new Error('Failed to reload user list.');

      const updatedList = await updatedListRes.json();
      setUsers(updatedList);
    } catch (err: any) {
      console.error('Error deleting user:', err);
      setUsers([...users, users.find((user) => user.Username === username)!]);
    }
  };

  const handleEdit = (index: number, field: string, value: string) => {
    const updated = [...users];
    updated[index][field] = value;
    setUsers(updated);
  };

  const handleAddUser = () => {
    navigate('/admin/generate');
  };

  if (loading) {
    return <div className="p-8 text-center">Loading users...</div>;
  }

  if (error) {
    return <div className="p-8 text-center text-red-600">{error}</div>;
  }

  // Determine ordered keys for table columns
  const preferredOrder = [
    "username",
    "password",
    "userType",
    "uploadUrl",
    "key",
    "xAmzAlgorithm",
    "xAmzCredential",
    "xAmzDate",
    "xAmzSignature",
    "policy"
  ];

  const allKeys = Array.from(
    new Set(users.flatMap((user) => Object.keys(user)))
  );

  const columns = preferredOrder.concat(
    allKeys.filter((key) => !preferredOrder.includes(key))
  );


  return (
    <div className="min-h-screen px-6 py-8 bg-gray-50">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">User Management</h1>
        <button
          onClick={handleSignOut}
          className="text-sm text-blue-600 hover:underline"
        >
          Sign out
        </button>
      </div>

      <div className="overflow-x-auto bg-white shadow rounded-xl p-4">
        <table className="min-w-full text-sm">
          <thead className="text-left border-b">
            <tr>
              {columns.map((field) => (
                <th key={field} className="py-2 px-3 font-medium text-gray-700">
                  {field}
                </th>
              ))}
              <th className="py-2 px-3"></th>
            </tr>
          </thead>
          <tbody>
            {users.map((user, idx) => (
              <tr key={user.Username + idx} className="border-b hover:bg-gray-50">
                {columns.map((field) => (
                  <td key={field} className="py-2 px-3">
                    <input
                      value={user[field] || ''}
                      readOnly
                      className="w-full px-2 py-1 border rounded bg-transparent cursor-text text-gray-800"
                    />
                  </td>
                ))}
                <td className="py-2 px-3 text-right">
                  {(user.username || '').toLowerCase() !== 'pharmanest_aws' ? (
                    <button
                      onClick={() => handleDelete(user.username)}
                      className="text-red-600 hover:underline text-sm"
                    >
                      Delete
                    </button>
                  ) : (
                    <span className="text-gray-400 text-sm italic">Protected</span>
                  )}

                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-6">
        <button
          onClick={handleAddUser}
          className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
        >
          Add User
        </button>
      </div>
    </div>
  );
}
